import {
  AdditiveBlending,
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  Points,
  ShaderMaterial,
} from 'three';

// Sistema d'estrelles (THREE.Points) del cel nocturn. Punts nítids (nucli brillant) + GLOW (halo)
// lligat a la mida i la intensitat de cada estrella. Parpelleig amb període randomitzat per
// estrella (temps entre min-max) i intensitat randomitzada. Se situen a la volta del cel
// (hemisferi SUPERIOR, per sobre de l'horitzó). Scene controla l'opacitat: 1 de nit, 0 de dia.
//
// Randomització per estrella via aRand ∈ [0,1]:
//   aRand.x → mida (sizeMin..sizeMax) → també escala el glow
//   aRand.y → intensitat (intensityMin..intensityMax) → també escala el glow
//   aRand.z → període del parpelleig (twinkleTimeMin..Max)
//   aRand.w → fase del parpelleig
export const STAR_SETTINGS = {
  count: 3200,
  sizeMin: 1.0, sizeMax: 4.0,      // mida del nucli (px-ish)

  color: '#ffffff',                // base
  colorVariation: 0.18,            // variació càlid/fred per estrella

  brightness: 0.85,                // multiplicador global d'intensitat (estrelles discretes)
  intensityMin: 0.35, intensityMax: 1.0, // intensitat randomitzada per estrella

  // Parpelleig: cada estrella té el seu PERÍODE (segons) entre min i max + fase pròpia.
  twinkleTimeMin: 1.5, twinkleTimeMax: 7.0,
  twinkleAmount: 0.55,             // 0 = sense parpelleig; 1 = molt

  // Glow (halo) — lligat a mida + intensitat de l'estrella.
  glowStrength: 0.4,               // intensitat del halo (discret)
  glowSize: 2.5,                   // com de gran és el halo respecte el nucli

  radius: 9000,                    // distància al cel (món)
  minElevationDeg: 2,              // NOMÉS per sobre de l'horitzó (cel, no mar)
};

const VERTEX = /* glsl */`
  uniform float uTime;
  uniform float uSizeMin, uSizeMax;
  uniform vec3 uColor;
  uniform float uColorVariation, uBrightness;
  uniform float uTwinkleTimeMin, uTwinkleTimeMax, uTwinkleAmount;
  uniform float uIntMin, uIntMax;
  uniform float uGlowStrength, uGlowSize;
  uniform float uOpacity;

  attribute vec4 aRand; // (mida, intensitat, període parpelleig, fase parpelleig)

  varying float vAlpha;
  varying vec3 vColor;
  varying float vGlow;
  varying float vCoreFrac;

  const float TAU = 6.2831853;

  void main() {
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);

    // Intensitat randomitzada per estrella.
    float intensity = mix(uIntMin, uIntMax, aRand.y);
    float bright = uBrightness * intensity;

    // Parpelleig: període (temps) randomitzat per estrella + fase pròpia.
    float period = mix(uTwinkleTimeMin, uTwinkleTimeMax, aRand.z);
    float flick = 0.5 + 0.5 * sin(uTime / max(period, 0.05) * TAU + aRand.w * TAU);
    float twinkle = 1.0 - uTwinkleAmount * flick;

    vAlpha = clamp(bright * twinkle * uOpacity, 0.0, 1.0);

    // Glow lligat a mida + intensitat: estrelles més grosses/brillants → més halo.
    float sizeN = mix(uSizeMin, uSizeMax, aRand.x);
    vGlow = uGlowStrength * intensity * (0.5 + 0.5 * aRand.x);

    // El quad creix per encabir el halo; el nucli ocupa la fracció central.
    gl_PointSize = sizeN * (1.0 + uGlowSize);
    vCoreFrac = 1.0 / (1.0 + uGlowSize);

    // Variació càlid/fred (temperatura) — hash barat (aRand.w ja s'usa per la fase).
    float h = fract(sin(aRand.x * 91.7 + aRand.y * 47.3) * 43758.5453) - 0.5;
    float temp = h * 2.0 * uColorVariation;
    vColor = clamp(uColor + vec3(temp, temp * 0.25, -temp), 0.0, 1.0);
  }
`;

const FRAGMENT = /* glsl */`
  precision highp float;

  uniform float uHdrBoost; // compensa l'exposició del compositor (via el color, HDR) perquè les
                           // estrelles no s'aixafin de nit (exposició baixa). El color va sense clamp.
  varying float vAlpha;
  varying vec3 vColor;
  varying float vGlow;
  varying float vCoreFrac;

  void main() {
    float d = length(gl_PointCoord - 0.5) * 2.0; // 0 centre, 1 vora
    if (d > 1.0) discard;
    // Nucli nítid dins la fracció central del quad.
    float core = pow(clamp(1.0 - d / vCoreFrac, 0.0, 1.0), 2.5);
    // Halo suau (glow) a tot el quad.
    float glow = pow(1.0 - d, 2.5) * vGlow;
    float a = (core + glow) * vAlpha;
    if (a <= 0.001) discard;
    gl_FragColor = vec4(vColor * uHdrBoost, a);
  }
`;

export default class StarField {
  constructor(opts = {}) {
    if (opts.settings) Object.assign(STAR_SETTINGS, opts.settings);
    this.points = null;
    this.geometry = null;
    this.material = null;
    this._opacity = 1;
    this._sig = '';
  }

  init() {
    const s = STAR_SETTINGS;
    this.material = new ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uSizeMin: { value: s.sizeMin }, uSizeMax: { value: s.sizeMax },
        uColor: { value: new Color(s.color) },
        uColorVariation: { value: s.colorVariation },
        uBrightness: { value: s.brightness },
        uIntMin: { value: s.intensityMin }, uIntMax: { value: s.intensityMax },
        uTwinkleTimeMin: { value: s.twinkleTimeMin }, uTwinkleTimeMax: { value: s.twinkleTimeMax },
        uTwinkleAmount: { value: s.twinkleAmount },
        uGlowStrength: { value: s.glowStrength }, uGlowSize: { value: s.glowSize },
        uOpacity: { value: this._opacity },
        uHdrBoost: { value: 1 },
      },
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: AdditiveBlending,
    });

    this.geometry = this._buildGeometry();
    this._sig = this._buildSig();
    this.points = new Points(this.geometry, this.material);
    this.points.frustumCulled = false;
    this.points.renderOrder = 10; // per sobre de la volta del cel
    return this.points;
  }

  // Sembra les estrelles a l'hemisferi SUPERIOR d'una esfera de radi `radius`, només on
  // l'elevació ≥ minElevationDeg. Mostreig uniforme del casquet: sinEl uniforme + azimut uniforme.
  _buildGeometry() {
    const s = STAR_SETTINGS;
    const n = Math.max(1, Math.floor(s.count));
    const pos = new Float32Array(n * 3);
    const rand = new Float32Array(n * 4);
    const minY = Math.sin((s.minElevationDeg * Math.PI) / 180);

    for (let i = 0; i < n; i++) {
      const az = Math.random() * Math.PI * 2;
      const sinEl = minY + Math.random() * (1 - minY);
      const r = Math.sqrt(Math.max(0, 1 - sinEl * sinEl));
      pos[i * 3] = Math.cos(az) * r * s.radius;
      pos[i * 3 + 1] = sinEl * s.radius;
      pos[i * 3 + 2] = Math.sin(az) * r * s.radius;

      rand[i * 4] = Math.random();     // mida
      rand[i * 4 + 1] = Math.random(); // intensitat
      rand[i * 4 + 2] = Math.random(); // període parpelleig
      rand[i * 4 + 3] = Math.random(); // fase parpelleig
    }

    const geo = new BufferGeometry();
    geo.setAttribute('position', new Float32BufferAttribute(pos, 3));
    geo.setAttribute('aRand', new Float32BufferAttribute(rand, 4));
    return geo;
  }

  loop(dt = 0.016) {
    if (this.material) this.material.uniforms.uTime.value += dt;
  }

  // Opacitat global (Scene: 1 de nit, 0 de dia).
  setOpacity(v) {
    this._opacity = v;
    if (this.material) this.material.uniforms.uOpacity.value = v;
  }

  // Compensa l'exposició del compositor (Scene passa 1/exposició) perquè les estrelles
  // siguin visibles de nit tot i l'exposició baixa del cel.
  setHdrBoost(v) {
    if (this.material) this.material.uniforms.uHdrBoost.value = v;
  }

  // Signatura de la geometria (count/radius/minElevation), per detectar rebuild.
  _buildSig() {
    const s = STAR_SETTINGS;
    return [Math.floor(s.count), s.radius, s.minElevationDeg].join(',');
  }

  // Uniforms en viu; refà la geometria si canvia count/radius/minElevationDeg — comparant amb la
  // geometria construïda (_sig), NO amb partial (tweakpane ja ha mutat STAR_SETTINGS abans).
  setConfig(partial = {}) {
    Object.assign(STAR_SETTINGS, partial);

    if (this.material) {
      const u = this.material.uniforms;
      const s = STAR_SETTINGS;
      u.uSizeMin.value = s.sizeMin; u.uSizeMax.value = s.sizeMax;
      u.uColor.value.set(s.color);
      u.uColorVariation.value = s.colorVariation;
      u.uBrightness.value = s.brightness;
      u.uIntMin.value = s.intensityMin; u.uIntMax.value = s.intensityMax;
      u.uTwinkleTimeMin.value = s.twinkleTimeMin; u.uTwinkleTimeMax.value = s.twinkleTimeMax;
      u.uTwinkleAmount.value = s.twinkleAmount;
      u.uGlowStrength.value = s.glowStrength; u.uGlowSize.value = s.glowSize;
    }

    const sig = this._buildSig();
    if (this.points && sig !== this._sig) {
      this.geometry.dispose();
      this.geometry = this._buildGeometry();
      this.points.geometry = this.geometry;
      this._sig = sig;
    }
  }

  setLayer(n) { if (this.points) this.points.layers.set(n); }

  resize() { /* no depèn de la mida de pantalla */ }

  dispose() {
    this.geometry?.dispose();
    this.material?.dispose();
    this.geometry = null;
    this.material = null;
    this.points = null;
  }

  get settings() { return STAR_SETTINGS; }
}
